import Cookies from 'js-cookie';
import { Controller } from 'stimulus';
import events from 'lib/events';
import {
  getFormattedPageValue, triggerCustomEvent, ezFadeOut,
} from 'lib/helpers';
import SearchStateManager from 'lib/search-state';

const searchState = new SearchStateManager();

export default class SearchController extends Controller {
  connect() {
    this.currentlyAjaxing = false;
    this.searchOverlayHtml = '<div class="filters-overlay" data-search-loading="true"><div class="ez-spinner"></div></div>';

    this.addLoadingOverlay(true);

    window.addEventListener('PostSearchAjax', this.postAjaxEvent);
    window.addEventListener('SearchPaginationChange', this.searchPaginationEvent);
    window.addEventListener('Searched', this.performSearch);
    window.addEventListener('click', this.trackSearchClick);
    window.addEventListener('click', this.trackSponsoredSearchClick);
    window.addEventListener(events.search.apply_filters, this.applySearchFilters);
  }

  disconnect() {
    window.removeEventListener('PostSearchAjax', this.postAjaxEvent);
    window.removeEventListener('SearchPaginationChange', this.searchPaginationEvent);
    window.removeEventListener('Searched', this.performSearch);
    window.removeEventListener('click', this.trackSearchClick);
    window.removeEventListener('click', this.trackSponsoredSearchClick);
    window.removeEventListener(events.search.apply_filters, this.applySearchFilters);
  }

  applySearchFilters = () => {
    const searchInput = document.querySelector('#search');
    const queryString = searchState.getFlatQueryString();
    const term = this.normalizeTerm(searchInput.value);
    const { searchRoute, verifiedSearchRoute } = searchInput.dataset;
    const { protocol, host } = window.location;

    let newSearchUrl;

    if (this.usingPhrasebase) {
      if (this.usingUnverifiedPath) {
        newSearchUrl = `${protocol}//${host}/${searchRoute}?qterm=${term}`;
        if (queryString) {
          newSearchUrl += `&${queryString.replace(/^\?/, '')}`;
        }
      } else {
        newSearchUrl = `${protocol}//${host}/${verifiedSearchRoute}/${term}`;
        if (queryString) {
          newSearchUrl += queryString;
        }
      }
    } else {
      newSearchUrl = `${protocol}//${host}/${searchRoute}/${term}`;
      if (queryString) {
        newSearchUrl += queryString;
      }
    }

    Cookies.set('ezSearchType', 'internal');
    Turbolinks.visit(newSearchUrl);
  }

  postAjaxEvent = (event) => {
    this.postAjax(event.detail.dataHash);
  }

  searchPaginationEvent = (event) => {
    triggerCustomEvent('PaginationChanged', {
      page: event.detail.page,
      stateKey: 'search-state',
      extraStates: ['filterState'],
      ajaxEventName: 'PostSearchAjax',
    });
  }

  performSearch = (event) => {
    const { searchPath } = event.detail;
    const searchRoute = event.detail.searchRoute ? event.detail.searchRoute : this.searchRoute;
    const term = this.normalizeTerm(event.detail.term);

    let searchQuery;
    // If search contains new filter params
    if (event.detail.useSearchState) {
      searchQuery = searchState.getFlatQueryString();
    }

    let searchUrl;
    if (this.usingPhrasebase) {
      if (searchPath) {
        searchUrl = searchPath;
        if (searchQuery) {
          searchUrl += searchQuery;
        }
      } else {
        searchUrl = `/${searchRoute}?qterm=${term}`;
        if (searchQuery) {
          searchUrl += `&${searchQuery.replace(/^\?/, '')}`;
        }
      }
    } else {
      searchUrl = `/${searchRoute}/${term}`;
      if (searchQuery) {
        searchUrl += searchQuery;
      }
    }

    if (event.detail.searchInNewTab === true) {
      window.open(searchUrl, '_blank');
    } else {
      Turbolinks.visit(searchUrl);
    }
  }

  addLoadingOverlay(fadeOut) {
    const searchResultsContainer = document.querySelector('.search-results');

    if (!searchResultsContainer) {
      return;
    }

    searchResultsContainer.insertAdjacentHTML(
      'afterBegin',
      this.searchOverlayHtml,
    );

    if (fadeOut) {
      const filterLoadingOverlay = document.querySelector('[data-search-loading=true]');

      if (filterLoadingOverlay) {
        ezFadeOut(filterLoadingOverlay, 30, () => {
          filterLoadingOverlay.parentNode.removeChild(filterLoadingOverlay);
        });
      }
    }
  }

  toggleSearchFilters() {
    const toggleSearchFiltersEvent = new CustomEvent('search:toggle_search_filters');
    window.dispatchEvent(toggleSearchFiltersEvent);
  }

  postAjax = (dataHash) => {
    if (!this.currentlyAjaxing) {
      this.currentlyAjaxing = true;
      this.addLoadingOverlay();

      searchState.updateStateValue('page', dataHash.page);
      const queryParams = searchState.getFlatQueryString();

      if (this.searchType) {
        Cookies.set('ezSearchType', this.searchType);
      }

      if (this.usingPhrasebase && this.usingUnverifiedPath) {
        let url = `${window.location.pathname}?qterm=${this.searchTerm}`;
        const qs = queryParams.replace(/^\?/, '');
        if (qs) {
          url += `&${qs}`;
        }
        window.location.assign(url);
      } else {
        window.location.assign(window.location.pathname + queryParams);
      }
    }
  };

  footerPagerChange() {
    event.preventDefault(); // eslint-disable-line no-restricted-globals

    const curPageValue = document.querySelector(
      '[data-target="ez-num-pager.currentPage"]',
    );
    const nextPageValue = parseInt(getFormattedPageValue(curPageValue), 10) + 1;

    triggerCustomEvent('SearchPaginationChange', { page: nextPageValue });
  }

  trackSearchClick = (event) => {
    const gridItem = event.target.closest('.search-results .ez-search-results .ez-resource-grid__item');
    if (gridItem) {
      triggerCustomEvent(events.search.click, {
        resourceId: gridItem.getAttribute('data-item-id'),
        resourcePosition: gridItem.getAttribute('data-position'),
        resourcePro: gridItem.getAttribute('data-pro'),
        searchTerm: this.searchTerm,
      });
    }
  }

  trackSponsoredSearchClick = (event) => {
    const sponsoredGridItem = event.target.closest('.search-results .ez-sponsored-results .ez-resource-grid__item');

    if (sponsoredGridItem) {
      triggerCustomEvent(events.search.sponsored_click, {
        resourceId: '',
        resourcePosition: '',
        resourcePro: '',
        searchTerm: this.searchTerm,
        sponsored: true,
      });
    }
  }

  normalizeTerm(term) {
    return encodeURIComponent(term.toLowerCase().trim().replace(/ /g, '-'));
  }

  get searchRoute() {
    const searchInput = document.querySelector('#search');
    if (searchInput) {
      return searchInput.dataset.searchRoute;
    }

    return 'search';
  }

  get searchTerm() {
    const searchInfo = this.element.querySelector('[data-search-term]');
    if (searchInfo) {
      return searchInfo.getAttribute('data-search-term');
    }

    return '';
  }

  get searchType() {
    const searchInfo = this.element.querySelector('[data-search-type]');
    if (searchInfo) {
      return searchInfo.getAttribute('data-search-type');
    }

    return null;
  }

  get usingPhrasebase() {
    return this.element.getAttribute('data-search-phrasebase') === 'true';
  }

  get usingUnverifiedPath() {
    const { searchParams } = (new URL(document.location));
    return searchParams.get('qterm');
  }
}
